import type { InviteType, RsvpResponse } from '../types'
import { InviteBadge } from './InviteBadge'

interface PersonChoiceProps {
  name: string
  inviteType: InviteType
  value: RsvpResponse
  disabled: boolean
  onChange: (response: RsvpResponse) => void
}

const OPTIONS: ReadonlyArray<{ value: RsvpResponse; label: string }> = [
  { value: 'yes', label: 'Delighted to' },
  { value: 'no', label: 'Sadly cannot' },
]

/** One person's yes/no, as a proper radio group so it works from a keyboard. */
export function PersonChoice({ name, inviteType, value, disabled, onChange }: PersonChoiceProps) {
  return (
    <fieldset
      disabled={disabled}
      className="border-ink/10 border-t pt-4 first:border-t-0 first:pt-0"
    >
      <legend className="font-display text-lg">
        {name} <InviteBadge type={inviteType} />
      </legend>
      <div className="mt-2 flex gap-2">
        {OPTIONS.map((option) => {
          const id = `${name}-${option.value}`.replace(/\s+/g, '-')
          return (
            <div key={option.value} className="flex-1">
              {/* The input is visually hidden and the label is the control, so
                  the radio stays keyboard and screen-reader operable. */}
              <input
                type="radio"
                id={id}
                name={`response-${name}`}
                value={option.value}
                checked={value === option.value}
                onChange={() => onChange(option.value)}
                className="peer sr-only"
              />
              <label
                htmlFor={id}
                className="border-ink/15 hover:border-claret/50 peer-checked:border-claret peer-checked:bg-claret block cursor-pointer rounded-md border px-4 py-2.5 text-center text-sm transition-colors peer-checked:text-white peer-disabled:cursor-not-allowed peer-disabled:opacity-60"
              >
                {option.label}
              </label>
            </div>
          )
        })}
      </div>

      {/* Only for a day guest turning the day down. Evening numbers are not the
          constraint - the manor sits far fewer for the breakfast than turn up
          later - so this would be a needless worry on an evening invite.

          Announced politely rather than assertively: it appears in response to
          the guest's own click, so it is news but not an alarm. */}
      {inviteType === 'day' && value === 'no' && (
        <p role="status" aria-live="polite" className="text-ink-soft mt-2 text-xs">
          Daytime places are limited, so we may offer {firstName(name)}&rsquo;s to someone else. Do
          tell us if anything changes.
        </p>
      )}
    </fieldset>
  )
}

/** Enough of a name to be personal without repeating it in full. */
function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name
}
